/**
 * Phase 12 — Admin BookingDetail · Partner Network panels.
 *
 * A single read-only component that renders 8 panels for a
 * partner-attributed booking:
 *
 *   1. Partner Attribution
 *   2. Region Allocation
 *   3. Pricing
 *   4. Kit Workflow
 *   5. White-Label Report
 *   6. Commission Ledger
 *   7. Notification Events
 *   8. AWARE Water Health Record
 *
 * Data is fetched in one call from
 * `GET /api/admin/bookings/{ref}/partner-detail` (read-only).
 * The component renders cleanly for:
 *
 *   • Direct-Aware bookings (no partner) — every panel marks itself
 *     "not applicable" and renders a single calm row.
 *   • Review-required / missing-partner / inactive-partner cases —
 *     attribution panel surfaces a yellow warning chip without
 *     blocking the rest of the page.
 *   • Missing/null optional fields — every field falls back to "—".
 *
 * NO mutation endpoints are called from this component. Mutations
 * remain on their respective Phase pages (kit, commission, etc.)
 * which this component links into.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, MapPin, DollarSign, Package, FileText,
  Wallet, Bell, Droplets, AlertTriangle, Loader2, ExternalLink, Truck,
  RefreshCw,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU, formatDateTimeAU } from "@/lib/formatDateAU";

const fmtMoney = (n) => (n === null || n === undefined || isNaN(n))
  ? "—"
  : `$${Number(n).toFixed(2)}`;

const fmt = (v) => (v === null || v === undefined || v === "") ? "—" : v;

const STATUS_TONES = {
  active:     "bg-emerald-100 text-emerald-800",
  draft:      "bg-amber-100 text-amber-800",
  inactive:   "bg-slate-200 text-slate-700",
  paused:     "bg-amber-100 text-amber-800",
  suspended:  "bg-rose-100 text-rose-800",
};
const ATTR_TONES = {
  "fresh-link":             "bg-cyan-100 text-cyan-800",
  "stored-90d":             "bg-violet-100 text-violet-800",
  "region-match":           "bg-lime-100 text-lime-800",
  "region-review-required": "bg-amber-100 text-amber-800",
  "direct-scwt":            "bg-slate-200 text-slate-700",
};
const COMMISSION_TONES = {
  pending:   "bg-amber-100 text-amber-800",
  payable:   "bg-cyan-100 text-cyan-800",
  paid:      "bg-emerald-100 text-emerald-800",
  void:      "bg-slate-200 text-slate-700",
  reversed:  "bg-rose-100 text-rose-800",
};
const WARNING_LABELS = {
  partner_missing:    "Partner row is missing or has been deleted.",
  partner_inactive:   "Partner is inactive/paused/suspended — review attribution.",
  partner_draft:      "Partner is still in DRAFT status — admin review required.",
  review_required:    "Region allocation is REVIEW REQUIRED. No partner has been auto-assigned.",
};


function PanelShell({ icon: Icon, title, testId, headerExtra, children, applicable = true }) {
  return (
    <section
      data-testid={testId}
      className={`rounded-2xl bg-white border ${applicable ? "border-slate-200" : "border-slate-100 opacity-75"} p-5`}
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-cyan-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">{title}</h3>
        </div>
        {headerExtra}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children, testId }) {
  return (
    <div className="grid grid-cols-[140px,1fr] gap-3 py-1.5 text-sm" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-800 break-words">{children}</div>
    </div>
  );
}

function Chip({ children, tone = "bg-slate-100 text-slate-700", testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${tone}`}
    >
      {children}
    </span>
  );
}

function NotApplicable({ message = "Not applicable to this booking." }) {
  return (
    <div className="text-xs text-slate-400 italic py-1">{message}</div>
  );
}


// ── 1. Partner Attribution ──────────────────────────────────────────────
function PartnerAttributionPanel({ data }) {
  const warn = data?.warning;
  return (
    <PanelShell
      icon={Users}
      title="Partner Attribution"
      testId="ph12-attribution-panel"
      applicable={data?.applicable}
      headerExtra={
        <Chip
          testId="ph12-attribution-source-chip"
          tone={ATTR_TONES[data?.attribution_source_label] || "bg-slate-100 text-slate-700"}
        >
          {data?.attribution_source_label || "direct-scwt"}
        </Chip>
      }
    >
      {!data?.applicable && data?.attribution_source_label === "direct-scwt" ? (
        <div
          data-testid="ph12-attribution-direct-scwt"
          className="text-xs text-slate-500 italic py-1"
        >
          Direct Aware booking — no partner attribution.
        </div>
      ) : (
        <>
          <Row label="Business" testId="ph12-attribution-business">
            {fmt(data?.partner_business_name)}{" "}
            {data?.partner_status && (
              <Chip
                tone={STATUS_TONES[data.partner_status] || "bg-slate-100 text-slate-700"}
                testId="ph12-attribution-partner-status"
              >
                {data.partner_status}
              </Chip>
            )}
          </Row>
          <Row label="Slug" testId="ph12-attribution-slug">
            <span className="font-mono text-xs">{fmt(data?.partner_slug)}</span>
          </Row>
          <Row label="Region" testId="ph12-attribution-region">
            {fmt(data?.partner_franchise_region)}{data?.partner_state ? ` · ${data.partner_state}` : ""}
          </Row>
          <Row label="First click">{data?.first_click_at ? formatDateTimeAU(data.first_click_at) : "—"}</Row>
          <Row label="Last click">{data?.last_click_at ? formatDateTimeAU(data.last_click_at) : "—"}</Row>
          {data?.referral_channel_label && (
            <Row label="Channel" testId="ph12-attribution-channel">
              {data.referral_channel_label}
            </Row>
          )}
          {(data?.attribution_source || data?.attribution_campaign) && (
            <Row label="Source / Campaign" testId="ph12-attribution-source-campaign">
              {(data.attribution_source || "—") + " / " + (data.attribution_campaign || "—")}
            </Row>
          )}
          {data?.first_touch_seen_at && (
            <Row label="First touch" testId="ph12-attribution-first-touch">
              {(data.first_touch_partner_slug || "—")} · {(data.first_touch_source || "—")}/{(data.first_touch_campaign || "—")} · {formatDateTimeAU(data.first_touch_seen_at)}
            </Row>
          )}
          {data?.last_touch_seen_at && (
            <Row label="Last touch" testId="ph12-attribution-last-touch">
              {(data.last_touch_partner_slug || "—")} · {(data.last_touch_source || "—")}/{(data.last_touch_campaign || "—")} · {formatDateTimeAU(data.last_touch_seen_at)}
            </Row>
          )}
          <Row label="Expires">{data?.expires_at ? formatDateTimeAU(data.expires_at) : "—"}</Row>
          {data?.source_url && (
            <Row label="Source URL" testId="ph12-attribution-source-url">
              <a
                href={data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-700 hover:underline text-xs break-all"
              >{data.source_url}</a>
            </Row>
          )}
          {data?.utm && Object.keys(data.utm).length > 0 && (
            <Row label="UTM" testId="ph12-attribution-utm">
              <div className="font-mono text-[11px] text-slate-700">
                {Object.entries(data.utm).map(([k, v]) => (
                  <div key={k}><span className="text-slate-500">{k}=</span>{String(v)}</div>
                ))}
              </div>
            </Row>
          )}
          {warn && (
            <div
              data-testid="ph12-attribution-warning"
              className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{WARNING_LABELS[warn] || warn}</span>
            </div>
          )}
        </>
      )}
    </PanelShell>
  );
}


// ── 2. Region Allocation ────────────────────────────────────────────────
function RegionAllocationPanel({ data }) {
  return (
    <PanelShell
      icon={MapPin}
      title="Region Allocation"
      testId="ph12-region-panel"
      applicable={data?.applicable}
      headerExtra={
        data?.review_required ? (
          <Chip tone="bg-amber-100 text-amber-800" testId="ph12-region-review-chip">
            review required
          </Chip>
        ) : data?.method ? (
          <Chip tone="bg-lime-100 text-lime-800" testId="ph12-region-method-chip">
            {data.method.replaceAll("_", " ")}
          </Chip>
        ) : null
      }
    >
      {!data?.applicable ? (
        <NotApplicable message="No region-allocation flow ran for this booking." />
      ) : (
        <>
          <Row label="Suburb">{fmt(data?.matched_suburb)}</Row>
          <Row label="Postcode">{fmt(data?.matched_postcode)}</Row>
          {data?.distance_km !== null && data?.distance_km !== undefined && (
            <Row label="Distance" testId="ph12-region-distance">
              {data.distance_km} km
              {data?.nearest_service_postcode
                ? ` (to service postcode ${data.nearest_service_postcode})`
                : data?.nearest_service_suburb
                ? ` (to service suburb ${data.nearest_service_suburb})`
                : ""}
            </Row>
          )}
          <Row label="Confidence">
            {data?.confidence === null || data?.confidence === undefined
              ? "—"
              : Number.isFinite(Number(data.confidence))
              ? `${Math.round(Number(data.confidence) * 100)}%`
              : String(data.confidence).replaceAll("_", " ")}
          </Row>
          <Row label="Candidates" testId="ph12-region-candidates">
            {(data?.candidate_partners || []).length === 0 ? "—" : (
              <ul className="space-y-1">
                {(data.candidate_partners || []).map((p) => (
                  <li key={p.id} className="text-xs">
                    <span className="font-mono text-slate-600">{p.partner_slug}</span>{" "}
                    <span className="text-slate-700">— {p.business_name}</span>{" "}
                    <Chip tone={STATUS_TONES[p.pws_partner_status] || "bg-slate-100 text-slate-700"}>
                      {p.pws_partner_status}
                    </Chip>
                  </li>
                ))}
              </ul>
            )}
          </Row>
          {data?.allocation_basis && (
            <div
              data-testid="ph12-region-allocation-basis"
              className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700"
            >
              {data.allocation_basis}
            </div>
          )}
          {data?.review_required && (
            <div
              data-testid="ph12-region-review-warning"
              className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Region matched multiple eligible partners (or none cleanly). Admin
                review required — no automatic assignment was made.
              </span>
            </div>
          )}
          <div className="mt-3">
            <Link
              to="/admin/partners/network"
              data-testid="ph12-region-link-to-network"
              className="text-[11px] font-semibold text-cyan-700 hover:underline inline-flex items-center gap-1"
            >
              Open PWS Network review queue <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </PanelShell>
  );
}


// ── 3. Pricing ──────────────────────────────────────────────────────────
function PricingPanel({ data }) {
  return (
    <PanelShell
      icon={DollarSign}
      title="Pricing"
      testId="ph12-pricing-panel"
      applicable={data?.applicable}
      headerExtra={
        data?.pricing_mode ? (
          <Chip
            tone={data.pricing_mode === "national_partner"
              ? "bg-cyan-100 text-cyan-800"
              : data.pricing_mode === "admin_override"
                ? "bg-amber-100 text-amber-800"
                : "bg-slate-200 text-slate-700"}
            testId="ph12-pricing-mode-chip"
          >
            {data.pricing_mode.replaceAll("_", " ")}
          </Chip>
        ) : null
      }
    >
      {!data?.applicable ? (
        <NotApplicable message="Pre-Phase-9 booking — no pricing audit recorded." />
      ) : (
        <>
          <Row label="Final price" testId="ph12-pricing-final">{fmtMoney(data?.final_price)}</Row>
          <Row label="Local baseline" testId="ph12-pricing-local-baseline">
            {fmtMoney(data?.local_retail_baseline)}
          </Row>
          <Row label="Kit postage" testId="ph12-pricing-kit-postage">
            {fmtMoney(data?.kit_postage_allowance)}
          </Row>
          <Row label="Partner margin" testId="ph12-pricing-partner-margin">
            {fmtMoney(data?.partner_margin_allowance)}
          </Row>
          <Row label="Source">{fmt(data?.price_source)}</Row>
          <Row label="Version">{fmt(data?.price_version)}</Row>
          {data?.admin_price_override && (
            <div
              data-testid="ph12-pricing-admin-override-warning"
              className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Admin price override is active.{" "}
                {data.admin_price_override_reason && (
                  <span className="font-semibold">{data.admin_price_override_reason}</span>
                )}
              </span>
            </div>
          )}
        </>
      )}
    </PanelShell>
  );
}


// ── 4. Kit Workflow ─────────────────────────────────────────────────────
function KitWorkflowPanel({ data }) {
  return (
    <PanelShell
      icon={Package}
      title="Kit Workflow"
      testId="ph12-kit-panel"
      applicable={data?.applicable}
      headerExtra={
        data?.kit_status ? (
          <Chip
            tone={data.kit_status === "returned"
              ? "bg-emerald-100 text-emerald-800"
              : data.kit_status === "sent"
                ? "bg-cyan-100 text-cyan-800"
                : data.kit_status === "not_required"
                  ? "bg-slate-200 text-slate-700"
                  : "bg-amber-100 text-amber-800"}
            testId="ph12-kit-status-chip"
          >
            {data.kit_status.replaceAll("_", " ")}
          </Chip>
        ) : null
      }
    >
      {!data?.applicable ? (
        <NotApplicable message="No kit workflow recorded for this booking." />
      ) : (
        <>
          <Row label="Required" testId="ph12-kit-required">
            {data?.kit_required ? "Yes" : "No"}
          </Row>
          <Row label="Method" testId="ph12-kit-method">{fmt(data?.kit_method)}</Row>
          <Row label="Sent" testId="ph12-kit-sent-date">
            {data?.kit_sent_date ? formatDateAU(data.kit_sent_date) : "—"}
          </Row>
          <Row label="Out tracking" testId="ph12-kit-out-tracking">
            <span className="font-mono text-xs">{fmt(data?.kit_tracking_number)}</span>
          </Row>
          <Row label="Return tracking" testId="ph12-kit-return-tracking">
            <span className="font-mono text-xs">{fmt(data?.return_tracking_number)}</span>
          </Row>
          <Row label="Sample received" testId="ph12-kit-sample-received">
            {data?.sample_received_date ? formatDateAU(data.sample_received_date) : "—"}
          </Row>
          {(data?.kit_audit_log || []).length > 0 && (
            <details className="mt-2" data-testid="ph12-kit-audit-log">
              <summary className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 cursor-pointer">
                Kit audit log ({data.kit_audit_log.length})
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto text-[11px] text-slate-700">
                {data.kit_audit_log.map((a, i) => (
                  <li key={i} className="border-l-2 border-slate-200 pl-2">
                    <span className="font-semibold">{a.action || "—"}</span>
                    {" · "}
                    <span className="text-slate-500">{a.actor_email || a.actor || "—"}</span>
                    {" · "}
                    <span className="text-slate-400">{a.ts ? formatDateTimeAU(a.ts) : "—"}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </PanelShell>
  );
}


// ── 5. White-Label Report ───────────────────────────────────────────────
function WhiteLabelReportPanel({ data }) {
  const coa = data?.coa;
  const summary = data?.summary_report;
  return (
    <PanelShell
      icon={FileText}
      title="Reports (COA + Summary)"
      testId="ph12-report-panel"
      applicable={data?.applicable}
    >
      {!data?.applicable ? (
        <NotApplicable message="No reports recorded for this booking yet." />
      ) : (
        <>
          {/* COA — always SCWT-branded */}
          <div data-testid="ph12-report-coa-block" className="rounded-lg bg-slate-50 p-3 mb-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
                Certificate of Analysis (SCWT-branded)
              </div>
              {coa?.status && (
                <Chip
                  testId="ph12-report-coa-status-chip"
                  tone={coa.status === "published"
                    ? "bg-emerald-100 text-emerald-800"
                    : coa.status === "approved"
                      ? "bg-cyan-100 text-cyan-800"
                      : "bg-amber-100 text-amber-800"}
                >
                  {coa.status.replaceAll("_", " ")}
                </Chip>
              )}
            </div>
            <Row label="Health score">
              {coa?.health_score !== null && coa?.health_score !== undefined ? coa.health_score : "—"}
              {coa?.band ? ` (${coa.band})` : ""}
            </Row>
            <Row label="Published">
              {coa?.published_at ? formatDateTimeAU(coa.published_at) : "—"}
            </Row>
          </div>

          {/* Summary Report — white-label aware */}
          <div data-testid="ph12-report-summary-block" className="rounded-lg bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
                White-Label Summary Report
              </div>
              {summary?.summary_branding_mode && (
                <Chip
                  testId="ph12-report-branding-chip"
                  tone={summary.summary_branding_mode === "white_label"
                    ? "bg-violet-100 text-violet-800"
                    : "bg-slate-200 text-slate-700"}
                >
                  {summary.summary_branding_mode.replaceAll("_", " ")}
                </Chip>
              )}
            </div>
            {!summary ? (
              <div className="text-xs text-slate-500 italic">No Summary Report generated yet.</div>
            ) : (
              <>
                <Row label="Status">
                  {summary.status === "published" ? (
                    <Chip tone="bg-emerald-100 text-emerald-800">published</Chip>
                  ) : (
                    fmt(summary.status)
                  )}
                </Row>
                <Row label="Version">v{fmt(summary.version)}</Row>
                <Row label="Generated">
                  {summary.generated_at ? formatDateTimeAU(summary.generated_at) : "—"}
                </Row>
                {summary.summary_branding_mode === "white_label" && (
                  <Row label="Branding partner" testId="ph12-report-summary-partner">
                    <span className="font-mono text-xs">{fmt(summary.summary_partner_id)}</span>
                  </Row>
                )}
                {summary.white_label_fallback_reason && (
                  <div
                    data-testid="ph12-report-fallback-warning"
                    className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900"
                  >
                    <span className="font-semibold">White-label fallback:</span>{" "}
                    {summary.white_label_fallback_reason}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </PanelShell>
  );
}


// ── 6. Commission Ledger ────────────────────────────────────────────────
function CommissionPanel({ data, reference, onBackfilled }) {
  const [backfilling, setBackfilling] = useState(false);
  const canBackfill = (
    !!reference
    && data?.applicable === true
    && data?.eligible === false
    && data?.ineligible_reason === "no_ledger_entry_yet"
  );
  const runBackfill = async () => {
    if (!reference) return;
    setBackfilling(true);
    try {
      const { data: result } = await api.post(
        `/admin/pws-commissions/backfill/${reference}`,
      );
      if (result?.created) {
        toast.success("Commission ledger entry created.");
      } else if (result?.skipped_existing) {
        toast.info("Ledger entry already existed — refreshed.");
      } else {
        toast.warning(
          `Backfill did not create an entry: ${result?.skipped_reason || "unknown reason"}`,
        );
      }
      onBackfilled?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };
  return (
    <PanelShell
      icon={Wallet}
      title="Commission Ledger"
      testId="ph12-commission-panel"
      applicable={data?.applicable}
      headerExtra={
        data?.commission_status ? (
          <Chip
            tone={COMMISSION_TONES[data.commission_status] || "bg-slate-100 text-slate-700"}
            testId="ph12-commission-status-chip"
          >
            {data.commission_status}
          </Chip>
        ) : null
      }
    >
      {!data?.applicable && !data?.eligible ? (
        <NotApplicable message="Not commission-eligible (local Aware pricing or admin override)." />
      ) : !data?.eligible ? (
        <>
          <div
            data-testid="ph12-commission-ineligible"
            className="text-xs text-slate-600 italic py-1"
          >
            No ledger entry yet — reason: <span className="font-semibold text-slate-700">{data.ineligible_reason}</span>
          </div>
          {canBackfill && (
            <div className="mt-3">
              <button
                type="button"
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="ph12-commission-backfill-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-60 shadow-sm"
              >
                {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {backfilling ? "Backfilling…" : "Backfill entry"}
              </button>
              <div className="mt-1 text-[10px] text-slate-500 leading-snug">
                Idempotent — creates the missing ledger row from the paid booking. Status transitions (approve, pay, void) stay on the Commission Ledger page.
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <Row label="Ledger ID" testId="ph12-commission-ledger-id">
            <span className="font-mono text-xs">{fmt(data?.ledger_id)}</span>
          </Row>
          <Row label="Amount" testId="ph12-commission-amount">
            {fmtMoney(data?.commission_amount)}
          </Row>
          <Row label="Basis">{fmt(data?.commission_basis)}</Row>
          <Row label="Margin reserved" testId="ph12-commission-margin">
            {fmtMoney(data?.partner_margin_allowance)}
          </Row>
          {data?.cap_applied && (
            <Row label="Cap applied">
              <Chip tone="bg-amber-100 text-amber-800">Yes</Chip>
            </Row>
          )}
          <Row label="Created">
            {data?.created_at ? formatDateTimeAU(data.created_at) : "—"}
          </Row>
          {data?.paid_at && (
            <Row label="Paid" testId="ph12-commission-paid">
              {formatDateTimeAU(data.paid_at)}{data.paid_by ? ` · ${data.paid_by}` : ""}
            </Row>
          )}
          {data?.audit_log_count > 0 && data?.last_audit && (
            <Row label="Last status" testId="ph12-commission-last-audit">
              <span className="text-xs text-slate-700">
                {fmt(data.last_audit.from_status)} → <strong>{fmt(data.last_audit.to_status)}</strong>
                {data.last_audit.reason ? ` — "${data.last_audit.reason}"` : ""}
                {" · "}
                <span className="text-slate-500">{data.last_audit.changed_at ? formatDateTimeAU(data.last_audit.changed_at) : "—"}</span>
              </span>
            </Row>
          )}
          <div className="mt-3">
            <Link
              to={`/admin/partners/commissions?booking=${encodeURIComponent(reference || "")}`}
              data-testid="ph12-commission-link-to-ledger"
              className="text-[11px] font-semibold text-cyan-700 hover:underline inline-flex items-center gap-1"
            >
              View in Commissions <ExternalLink className="w-3 h-3" />
            </Link>
            <div className="mt-1 text-[10px] text-slate-500 leading-snug">
              Read-only here. Status transitions (approve, pay, void) live on the Commission Ledger page.
            </div>
          </div>
        </>
      )}
    </PanelShell>
  );
}


// ── 7. Notification Events ──────────────────────────────────────────────
function NotificationsPanel({ data }) {
  const events = data?.events || [];
  return (
    <PanelShell
      icon={Bell}
      title="Notifications"
      testId="ph12-notifications-panel"
      applicable={data?.applicable}
      headerExtra={
        events.length > 0 ? (
          <Chip tone="bg-cyan-100 text-cyan-800" testId="ph12-notifications-count-chip">
            {events.length} event{events.length === 1 ? "" : "s"}
          </Chip>
        ) : null
      }
    >
      {events.length === 0 ? (
        <div
          data-testid="ph12-notifications-empty"
          className="text-xs text-slate-500 italic py-1"
        >
          No notification events recorded for this booking.
        </div>
      ) : (
        <>
          <Row label="Last event">
            {data?.last_event_at ? formatDateTimeAU(data.last_event_at) : "—"}
          </Row>
          <ul className="mt-2 space-y-1.5 text-xs max-h-60 overflow-y-auto" data-testid="ph12-notifications-event-list">
            {events.map((e) => (
              <li
                key={e.event_id}
                data-testid={`ph12-notifications-event-${e.event_id}`}
                className="border border-slate-100 rounded-md p-2 bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-700">{e.label || e.event_type}</span>
                  <Chip
                    tone={e.dispatch_status === "would_send"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-200 text-slate-600"}
                    testId={`ph12-notifications-dispatch-${e.event_id}`}
                  >
                    {e.dispatch_status || "skipped"}
                  </Chip>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {e.recorded_at ? formatDateTimeAU(e.recorded_at) : "—"}
                  {e.dispatch_reason ? ` · ${e.dispatch_reason}` : ""}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Link
              to="/admin/partners/notifications"
              data-testid="ph12-notifications-link-to-log"
              className="text-[11px] font-semibold text-cyan-700 hover:underline inline-flex items-center gap-1"
            >
              Open Notification Log <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </PanelShell>
  );
}


// ── 8. AWARE Water Health Record ───────────────────────────────────────────────────
function WaterPassportPanel({ data }) {
  return (
    <PanelShell
      icon={Droplets}
      title="AWARE Water Health Record"
      testId="ph12-passport-panel"
      applicable={data?.applicable}
    >
      {!data?.applicable ? (
        <NotApplicable message="No AWARE Water Health Record linked to this booking." />
      ) : (
        <>
          <Row label="Property" testId="ph12-passport-property">
            {fmt(data?.property_address)}
          </Row>
          <Row label="Latest score" testId="ph12-passport-health-score">
            {data?.latest_health_score !== null && data?.latest_health_score !== undefined
              ? data.latest_health_score
              : "—"}
            {data?.latest_band ? ` (${data.latest_band})` : ""}
          </Row>
          <Row label="Published">
            {data?.latest_published_at ? formatDateTimeAU(data.latest_published_at) : "—"}
          </Row>
          <Row label="Collected">
            {data?.latest_collected_at ? formatDateAU(data.latest_collected_at) : "—"}
          </Row>
          <Row label="Retest due" testId="ph12-passport-retest-due">
            {data?.retest_due_at ? formatDateAU(data.retest_due_at) : "—"}
          </Row>
          {data?.admin_link && (
            <div className="mt-3">
              <Link
                to={data.admin_link}
                data-testid="ph12-passport-admin-link"
                className="text-[11px] font-semibold text-cyan-700 hover:underline inline-flex items-center gap-1"
              >
                Open admin property detail <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          )}
        </>
      )}
    </PanelShell>
  );
}


export default function PartnerNetworkPanels({ reference }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    if (!reference) return;
    try {
      const { data } = await api.get(`/admin/bookings/${reference}/partner-detail`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!reference) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/admin/bookings/${reference}/partner-detail`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reference]);

  if (loading) {
    return (
      <div
        data-testid="ph12-partner-detail-loading"
        className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-2 text-slate-400 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading partner network detail…
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="ph12-partner-detail-error"
        className="mt-6 rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800"
      >
        Failed to load partner network panels: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <section
      data-testid="ph12-partner-network-panels"
      className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4"
    >
      <PartnerAttributionPanel data={data.partner_attribution} />
      <RegionAllocationPanel    data={data.region_allocation} />
      <PricingPanel             data={data.pricing} />
      <KitWorkflowPanel         data={data.kit_workflow} />
      <WhiteLabelReportPanel    data={data.white_label_report} />
      <CommissionPanel          data={data.commission} reference={reference} onBackfilled={reload} />
      <NotificationsPanel       data={data.notifications} />
      <WaterPassportPanel       data={data.water_passport} />
      <SampleCollectionPanel    reference={reference} />
    </section>
  );
}


function SampleCollectionPanel({ reference }) {
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/pws-sample-collection/booking/${reference}`)
      .then(({ data }) => { if (alive) setPanel(data); })
      .catch(() => { if (alive) setPanel(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reference]);

  if (loading) {
    return (
      <div
        data-testid="ph30-bd-loading"
        className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-2 text-slate-400 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading sample collection…
      </div>
    );
  }
  if (!panel) return null;

  if (!panel.applicable) {
    return (
      <div
        data-testid="ph30-bd-panel"
        className="rounded-2xl bg-white border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            Optional Sample Collection
          </h3>
        </div>
        <div
          data-testid="ph30-bd-not-applicable"
          className="text-xs text-slate-500"
        >
          Not applicable for this booking ({fmt(panel.reason)}).
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="ph30-bd-panel"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-cyan-600" />
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Optional Sample Collection
          </h3>
        </div>
        <span
          data-testid="ph30-bd-status"
          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
            panel.available
              ? "bg-emerald-100 text-emerald-800"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {panel.available ? "Available" : "Not available"}
        </span>
      </div>
      <dl className="text-xs space-y-1.5 text-slate-700">
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Partner</dt>
          <dd className="text-right">{fmt(panel.partner_business)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Customer postcode</dt>
          <dd className="text-right">{fmt(panel.customer_postcode)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Area match</dt>
          <dd className="text-right">{panel.area_match ? "Yes" : "No"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Reason</dt>
          <dd
            data-testid="ph30-bd-reason"
            className="text-right font-mono text-[10px]"
          >
            {fmt(panel.reason)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Price mode</dt>
          <dd className="text-right">{fmt(panel.price_mode)}</dd>
        </div>
        {panel.price_mode === "admin_configured_price" ? (
          <div className="flex justify-between gap-2">
            <dt className="text-slate-500">Configured price</dt>
            <dd className="text-right">{fmtMoney(panel.configured_price)}</dd>
          </div>
        ) : null}
        {panel.partner_note ? (
          <div className="pt-1">
            <dt className="text-slate-500">Partner note</dt>
            <dd className="text-slate-700 italic mt-0.5">
              {panel.partner_note}
            </dd>
          </div>
        ) : null}
      </dl>
      <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-400">
        No dispatch · No job created · No schedule · No fee applied
      </div>
    </div>
  );
}
